// Content module for the Guides page (/guides).
//
// Marketing / desk edit THIS FILE ONLY — the page renders whatever is here.
// - Add a video: drop the YouTube video ID into the section's `videos` array.
//   An empty `videos` array renders a "video coming soon" placeholder.
// - Add an FAQ: append { q, a } to the section's `faqs` array.
// - Section `id` doubles as the URL anchor (e.g. /guides#watchlists) — used by
//   checklist tasks and empty states to deep-link. Don't rename ids casually.

export type GuideVideo = {
  /** YouTube video ID, e.g. "dQw4w9WgXcQ" (the bit after v= in the URL) */
  youtubeId: string;
  title: string;
};

export type GuideFaq = {
  q: string;
  a: string;
};

export type GuideSectionId =
  | "dashboard"
  | "watchlists"
  | "squawk"
  | "calendar"
  | "research"
  | "mobile";

export type GuideSection = {
  id: GuideSectionId;
  title: string;
  tagline: string;
  videos: GuideVideo[];
  faqs: GuideFaq[];
};

export const guideSections: GuideSection[] = [
  {
    id: "dashboard",
    title: "Your Dashboard",
    tagline: "Build a workspace around the feeds and tools you actually use",
    videos: [
      // { youtubeId: "", title: "Getting started with your dashboard" },
    ],
    faqs: [
      {
        q: "How do I add or remove widgets?",
        a: "Open the widget menu from the dashboard toolbar to browse every feed and tool available on your plan. Click a widget to add it to the current tab; use the X on any widget's header to remove it. Drag headers to rearrange and drag edges to resize — your layout saves automatically.",
      },
      {
        q: "Can I have more than one dashboard?",
        a: "Yes — use the + next to your tabs to create additional tabs. Many traders keep one tab per strategy or asset class, e.g. a macro tab and an equities tab.",
      },
      {
        q: "Can I open a widget in its own window?",
        a: "Yes. Use the pop-out action in a widget's header to move it to a separate browser window — useful for multi-monitor setups. Closing the window returns the widget to your dashboard.",
      },
      {
        q: "I broke my layout — can I start over?",
        a: "Default tabs can be reset from the tab menu, which restores the original layout without touching your watchlists or filters.",
      },
    ],
  },
  {
    id: "watchlists",
    title: "Watchlists",
    tagline: "Cut the noise: track your names, keywords and topics",
    videos: [],
    faqs: [
      {
        q: "What's the difference between filtering and highlighting?",
        a: "Filtering restricts a feed to only matching stories. Highlighting keeps the full feed flowing but visually emphasises stories that match your watchlist — most traders highlight on their main headline feed so they never miss broader market context, and filter on secondary feeds.",
      },
      {
        q: "How do I create a watchlist?",
        a: "Go to Watchlists in the sidebar and choose Build it myself. Type or paste tickers, companies and topics into one list — there's no type to pick first. Or open one of the Newsquawk library's watchlists and save your own version of it.",
      },
      {
        q: "What are combined watchlists?",
        a: "A combined watchlist joins several watchlists, topics and keywords with AND/OR logic into one — e.g. \"Fed OR ECB, AND my semis watchlist\". Build one from Watchlists, then filter or highlight with it like any other watchlist on feeds that support it.",
      },
      {
        q: "Can I get notified when a watchlist matches?",
        a: "Yes — each watchlist has notification settings for pop-up and sound alerts, so key stories reach you even when you're focused elsewhere.",
      },
    ],
  },
  {
    id: "squawk",
    title: "Audio Squawk",
    tagline: "Breaking news read to you in real time by the Newsquawk desk",
    videos: [],
    faqs: [
      {
        q: "How do I listen to the squawk?",
        a: "Click the squawk button at the top of the sidebar to connect. Audio starts within seconds — analysts call breaking headlines, data releases and market colour as they happen.",
      },
      {
        q: "The audio keeps cutting out — what can I do?",
        a: "The player automatically falls back through several transports if your network blocks one. If issues persist, check Settings for connection troubleshooting, or contact support via the chat bubble — corporate networks sometimes need an alternative transport enabled.",
      },
      {
        q: "Which channels do I have access to?",
        a: "Channels depend on your subscription — metals, energy, FX, fixed income, equities or multi-asset. The channel selector shows what's available on your plan.",
      },
    ],
  },
  {
    id: "calendar",
    title: "Economic Calendar",
    tagline: "Scheduled releases and live results, including exclusive Newsquawk events",
    videos: [],
    faqs: [
      {
        q: "What makes the Newsquawk calendar different?",
        a: "Alongside standard releases you get desk-written previews and exclusive Newsquawk events, with results delivered the moment they hit — the same feed our analysts squawk from.",
      },
      {
        q: "Can I see the calendar on my dashboard?",
        a: "Yes — add the Economic Calendar widget to any tab, or open the full-page calendar from the sidebar for a larger view with more filtering options.",
      },
    ],
  },
  {
    id: "research",
    title: "Research Suite",
    tagline: "Desk analysis, previews and reviews beyond the headlines",
    videos: [],
    faqs: [
      {
        q: "Where do I find Newsquawk research?",
        a: "Open Research Suite in the sidebar for the full library, or add the Research Reports widget to your dashboard to see notes as they publish.",
      },
      {
        q: "What is the Equities Intelligence Suite?",
        a: "A set of widgets covering SEC filings with AI summaries, unusual options activity, insider transactions, congressional trades and block trades. It's available as an add-on — you'll see an upgrade prompt on these widgets if they're not on your plan.",
      },
    ],
  },
  {
    id: "mobile",
    title: "Mobile App",
    tagline: "Squawk audio, headlines and alerts in your pocket",
    videos: [],
    faqs: [
      {
        q: "Where do I get the app?",
        a: "Search \"Newsquawk\" in the App Store or Google Play. Sign in with the same account you use here — your subscription carries over.",
      },
      {
        q: "Does the app do push notifications?",
        a: "Yes — enable notifications in the app to get alerts for important headlines even when the app is closed.",
      },
    ],
  },
];

/** Case-insensitive search across titles, questions and answers. */
export function filterGuideSections(
  sections: GuideSection[],
  query: string
): GuideSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;
  return sections
    .map((section) => {
      const sectionMatches =
        section.title.toLowerCase().includes(q) ||
        section.tagline.toLowerCase().includes(q);
      const faqs = section.faqs.filter(
        (f) =>
          f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
      );
      const videos = section.videos.filter((v) =>
        v.title.toLowerCase().includes(q)
      );
      if (!sectionMatches && faqs.length === 0 && videos.length === 0) {
        return null;
      }
      // Keep all content when the section itself matches; otherwise show hits.
      return sectionMatches ? section : { ...section, faqs, videos };
    })
    .filter((s): s is GuideSection => s !== null);
}
